
import React, { useState } from 'react';
import { SimulationConfig, BallSoundEffect, BallCollisionSoundType } from './types';
import { SectionLabel } from './SectionLabel';
import { AUDIO } from './services/AudioService';
import { ControlGroup } from './ControlGroup';

interface CustomSoundUploadSectionProps {
  config: SimulationConfig;
  onBallSoundUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isUploadingBallSound: boolean;
}

export const CustomSoundUploadSection: React.FC<CustomSoundUploadSectionProps> = ({ config, onBallSoundUpload, isUploadingBallSound }) => {
  const [activeSubTab, setActiveSubTab] = useState<'COLLISION' | 'BREAK'>('BREAK');
  
  const soundEffects: BallSoundEffect[] = [
    'Pop', 'Wood', 'Metal', 'Synth-Zap', 'Crystal', 'Soft-Thud', 'Explosion',
    'Laser-Gun', 'Pistol', 'Sniper', 'Glass-Break', 'Digital-Beep', 
    'Retro-Jump', 'Thunder', 'Bubble', 'Camera', 'Sword', 
    'Robot', 'Alien', 'Bass-Drop', 'Cowbell', 'Snare', 
    'Kick', 'Hi-Hat', 'Cyber-Pulse', 'Magic', 'Sparkle', 
    'Steam', 'Electric', 'Glitch', 'Heavy-Slam', 
    'Echo-Pluck', 'Sub-Boom', 'Whip', 'SciFi-Door', 'Wood-Block', 'Marimba'
  ];

  const removeUserSound = (id: string) => {
    const updated = config.userSounds.filter(s => s.id !== id);
    (window as any).setConfig((prev: SimulationConfig) => ({
        ...prev,
        userSounds: updated,
        selectedUserSoundId: prev.selectedUserSoundId === id ? undefined : prev.selectedUserSoundId,
        selectedBreakUserSoundId: prev.selectedBreakUserSoundId === id ? undefined : prev.selectedBreakUserSoundId
    }));
  };

  const renameUserSound = (id: string, currentName: string) => {
    const newName = prompt("Enter new name for sound effect:", currentName);
    if (newName === null) return;
    const finalName = newName.trim() || currentName;
    (window as any).setConfig((prev: SimulationConfig) => ({
      ...prev,
      userSounds: prev.userSounds.map(s => s.id === id ? { ...s, name: finalName } : s)
    }));
  };

  const handleSetSoundType = (type: BallCollisionSoundType) => {
    const key = activeSubTab === 'COLLISION' ? 'ballCollisionSoundType' : 'breakSoundType';
    (window as any).setConfig((prev: SimulationConfig) => ({ ...prev, [key]: type }));
  };

  const handleSelectSound = (eff: BallSoundEffect) => {
    const keyType = activeSubTab === 'COLLISION' ? 'ballCollisionSoundType' : 'breakSoundType';
    const keyEff = activeSubTab === 'COLLISION' ? 'selectedBallSoundEffect' : 'selectedBreakSoundEffect';
    (window as any).setConfig((prev: SimulationConfig) => ({ 
        ...prev, 
        [keyType]: 'SOUND_EFFECT',
        [keyEff]: eff
    }));
    AUDIO.playSoundEffect(eff, config, 0.5);
  };

  const handleSelectUserSound = (id: string) => {
    const keyType = activeSubTab === 'COLLISION' ? 'ballCollisionSoundType' : 'breakSoundType';
    const keyId = activeSubTab === 'COLLISION' ? 'selectedUserSoundId' : 'selectedBreakUserSoundId';
    const sound = config.userSounds.find(s => s.id === id);
    (window as any).setConfig((prev: SimulationConfig) => ({ 
        ...prev, 
        [keyType]: 'CUSTOM',
        [keyId]: id
    }));
    if (sound) {
        const source = sound.url || sound.base64;
        if (source) AUDIO.playCustomBuffer(source, 0.5, config);
    }
  };

  const currentType = activeSubTab === 'COLLISION' ? config.ballCollisionSoundType : config.breakSoundType;
  const currentEff = activeSubTab === 'COLLISION' ? config.selectedBallSoundEffect : config.selectedBreakSoundEffect;
  const currentUserSoundId = activeSubTab === 'COLLISION' ? config.selectedUserSoundId : config.selectedBreakUserSoundId;

  return (
    <div className="flex flex-col gap-3 p-4 rounded-3xl border bg-indigo-500/5 border-indigo-500/20">
      <SectionLabel icon="fa-music" darkMode={config.darkMode}>Sound Engine Manager</SectionLabel>
      
      <ControlGroup label="Target Mode" settingKey="soundEngineMode" darkMode={config.darkMode}>
        <div className={`grid grid-cols-2 gap-1 p-1 rounded-xl w-full ${config.darkMode ? 'bg-neutral-800' : 'bg-neutral-100'}`}>
          <button onClick={() => setActiveSubTab('COLLISION')} className={`text-[9px] font-black py-2 rounded-lg transition-all ${activeSubTab === 'COLLISION' ? 'bg-indigo-600 text-white shadow-sm' : 'text-neutral-500'}`}>Bounces</button>
          <button onClick={() => setActiveSubTab('BREAK')} className={`text-[9px] font-black py-2 rounded-lg transition-all ${activeSubTab === 'BREAK' ? 'bg-indigo-600 text-white shadow-sm' : 'text-neutral-500'}`}>Breaks</button>
        </div>
      </ControlGroup>

      <ControlGroup label="Sound Source" settingKey="soundSourceType" darkMode={config.darkMode}>
        <div className={`grid grid-cols-3 gap-1 p-1 rounded-xl w-full ${config.darkMode ? 'bg-neutral-800/50' : 'bg-neutral-200/50'}`}>
          <button onClick={() => handleSetSoundType('MELODY')} className={`text-[8px] font-black py-1.5 rounded-md transition-all ${currentType === 'MELODY' ? (config.darkMode ? 'bg-neutral-700 text-white' : 'bg-white shadow-xs') : 'text-neutral-500'}`}>Melody</button>
          <button onClick={() => handleSetSoundType('SOUND_EFFECT')} className={`text-[8px] font-black py-1.5 rounded-md transition-all ${currentType === 'SOUND_EFFECT' ? (config.darkMode ? 'bg-neutral-700 text-white' : 'bg-white shadow-xs') : 'text-neutral-500'}`}>FX</button>
          <button onClick={() => handleSetSoundType('CUSTOM')} className={`text-[8px] font-black py-1.5 rounded-md transition-all ${currentType === 'CUSTOM' ? (config.darkMode ? 'bg-neutral-700 text-white' : 'bg-white shadow-xs') : 'text-neutral-500'}`}>Uploads</button>
        </div>
      </ControlGroup>

      {currentType === 'SOUND_EFFECT' && (
        <ControlGroup label="Choose Effect" settingKey="selectedBallSoundEffect" darkMode={config.darkMode}>
            <select 
                value={currentEff} 
                onChange={e => handleSelectSound(e.target.value as BallSoundEffect)}
                className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`}
            >
                {soundEffects.map(s => <option key={s} value={s}>{s.replace('-', ' ')}</option>)}
            </select>
        </ControlGroup>
      )}

      {currentType === 'CUSTOM' && (
        <ControlGroup label="User Library" settingKey="userSoundLibrary" darkMode={config.darkMode}>
            <div className="flex flex-col gap-2 w-full">
                <label className={`w-full py-2 px-4 rounded-xl border text-[9px] font-black uppercase tracking-widest cursor-pointer flex items-center justify-center gap-2 transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700 hover:bg-neutral-700' : 'bg-white border-neutral-200 hover:bg-neutral-50'}`}>
                    <i className={`fa-solid ${isUploadingBallSound ? 'fa-spinner fa-spin' : 'fa-plus'}`}></i>
                    {isUploadingBallSound ? 'Uploading...' : 'Upload New'}
                    <input type="file" accept="audio/*" onChange={onBallSoundUpload} className="hidden" />
                </label>
                
                <div className="flex flex-col gap-1.5 max-h-48 overflow-y-auto custom-scrollbar mt-1">
                    {config.userSounds.length === 0 && (
                        <div className="text-center py-4 opacity-50 text-[10px] font-bold uppercase">Empty Library</div>
                    )}
                    {config.userSounds.map(s => (
                        <div key={s.id} className={`flex items-center justify-between p-2 rounded-lg border transition-all ${currentUserSoundId === s.id ? 'bg-indigo-600/20 border-indigo-500/50' : (config.darkMode ? 'bg-neutral-800/40 border-neutral-700' : 'bg-white border-neutral-200')}`}>
                            <div className="flex-1 flex flex-col min-w-0 pr-2 cursor-pointer" onClick={() => handleSelectUserSound(s.id)}>
                                <button className="text-[9px] font-black uppercase text-left truncate flex items-center gap-2">
                                    {s.name}
                                    {s.url && <i className="fa-solid fa-cloud text-cyan-500 text-[8px]" title="Cloud Hosted"></i>}
                                </button>
                                <span className="text-[7px] opacity-40 uppercase font-bold">{currentUserSoundId === s.id ? 'Active' : 'Click to set'}</span>
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => renameUserSound(s.id, s.name)} title="Rename" className="w-6 h-6 rounded-md bg-neutral-500/10 text-neutral-500 flex items-center justify-center text-[8px]"><i className="fa-solid fa-pen"></i></button>
                                <button onClick={() => {
                                    const src = s.url || s.base64;
                                    if(src) AUDIO.playCustomBuffer(src, 0.5, config);
                                }} title="Preview" className="w-6 h-6 rounded-md bg-indigo-500/10 text-indigo-500 flex items-center justify-center text-[8px]"><i className="fa-solid fa-play"></i></button>
                                <button onClick={() => removeUserSound(s.id)} title="Delete" className="w-6 h-6 rounded-md bg-red-500/10 text-red-500 flex items-center justify-center text-[8px]"><i className="fa-solid fa-trash-can"></i></button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </ControlGroup>
      )}
      
      {currentType === 'MELODY' && (
          <div className="p-4 rounded-xl border border-dashed border-neutral-500/30 text-center">
              <span className="text-[10px] font-bold uppercase opacity-80">Using Global Melody: {config.selectedMelody}</span>
          </div>
      )}
    </div>
  );
};
